import { CountryCode, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'
import { getCountry, getDialCode } from '../constants/countries'

// Validity is judged against the country the visitor picked: '9876543210' is a
// real number with India selected and not one with the US selected.
export const isValidPhoneForCountry = (phone: string, country: string): boolean => {
  const trimmed = phone.trim()
  if (!trimmed) return false
  try {
    return isValidPhoneNumber(trimmed, country as CountryCode)
  } catch {
    return false
  }
}

export const invalidPhoneMessage = (country: string): string => {
  const name = getCountry(country)?.name
  return name ? `Enter a valid phone number for ${name}.` : 'Enter a valid phone number.'
}

type NormalisedPhone = {
  // Country the number itself belongs to, if it was typed with a dial code.
  country?: CountryCode
  // The number without its dial code — what we store, since country_code is its own column.
  nationalNumber: string
}

// Splits what the visitor typed into country + national number, so a pasted
// '+971 50 123 4567' is stored as country AE and '501234567' rather than
// landing under whichever country the selector happened to be on.
export const normalisePhone = (phone: string, country: string): NormalisedPhone | undefined => {
  const trimmed = phone.trim()
  if (!trimmed) return undefined
  let parsed
  try {
    parsed = parsePhoneNumberFromString(trimmed, country as CountryCode)
  } catch {
    return undefined
  }
  if (!parsed) return undefined

  // Countries sharing a dial code (+44 GB/GG/JE/IM, +1 US/CA) can't be told
  // apart reliably, so keep the visitor's own choice in that case.
  const sameDialCode =
    !!parsed.country && getDialCode(parsed.country) === getDialCode(country)
  return {
    country: sameDialCode ? undefined : parsed.country,
    nationalNumber: parsed.nationalNumber,
  }
}
